/**
 * FirstActionStep — step 5 of the onboarding wizard.
 * Three CTA cards: Browse KB, Create Ticket, Start Learning Path.
 * Clicking "Start Learning Path" triggers onboarding completion.
 */

import { BookOpen, Ticket, GraduationCap, ArrowRight } from 'lucide-react';

interface ActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  primary?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}

function ActionCard({ title, description, icon, primary, isLoading, onClick }: ActionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      className={[
        'group flex flex-col items-start gap-3 rounded-xl border p-5 text-left',
        'transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-blue-500',
        'hover:scale-[1.02] hover:shadow-lg active:scale-100',
        primary
          ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
          : 'border-gray-200 bg-white hover:border-gray-300',
        isLoading ? 'cursor-not-allowed opacity-60' : '',
      ].join(' ')}
    >
      <div
        className={[
          'flex h-10 w-10 items-center justify-center rounded-lg',
          primary ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600',
        ].join(' ')}
      >
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{description}</p>
      </div>
      <ArrowRight
        className={[
          'h-4 w-4 transition-transform duration-150 group-hover:translate-x-1',
          primary ? 'text-blue-600' : 'text-gray-400',
        ].join(' ')}
        aria-hidden="true"
      />
    </button>
  );
}

interface FirstActionStepProps {
  isSubmitting: boolean;
  onStartLearningPath: () => void;
  onBrowseKB: () => void;
  onCreateTicket: () => void;
}

export function FirstActionStep({
  isSubmitting,
  onStartLearningPath,
  onBrowseKB,
  onCreateTicket,
}: FirstActionStepProps) {
  return (
    <div className="px-4 py-6">
      <div className="mb-6 text-center">
        <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl">
          🎉
        </div>
        <h2 className="text-xl font-semibold text-gray-900">You're all set!</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose how you'd like to start. You can always explore other areas later.
        </p>
      </div>

      {/* 3-column on desktop, 2-column on tablet, 1-column on mobile */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <ActionCard
          title="Browse Knowledge Base"
          description="Find articles, guides, and how-tos for your team."
          icon={<BookOpen className="h-5 w-5" />}
          onClick={onBrowseKB}
        />
        <ActionCard
          title="Create a Support Ticket"
          description="Got an issue? Our team is ready to help you resolve it."
          icon={<Ticket className="h-5 w-5" />}
          onClick={onCreateTicket}
        />
        <ActionCard
          title="Start Learning Path"
          description="Follow a guided path tailored to your role and interests."
          icon={<GraduationCap className="h-5 w-5" />}
          primary
          isLoading={isSubmitting}
          onClick={onStartLearningPath}
        />
      </div>
    </div>
  );
}
